import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../api';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button } from '../../components/Button';
import { formatCurrency } from '../../utils/currency';

interface PendingPayment {
  payment_id: string;
  payment_reference: string;
  amount: string;
  payment_method: string;
  payment_date: string;
  bank_name: string;
  transaction_reference: string;
  payment_proof_url: string;
  notes?: string;
  loan_reference: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  created_at: string;
}

export const PendingPayments: React.FC = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<PendingPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchPendingPayments();
  }, [page]);

  const fetchPendingPayments = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getPendingPayments(page, 20);

      if (response.success && response.data) {
        setPayments(response.data);
        setTotalPages(response.pagination?.totalPages || 1);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load pending payments');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyClick = (paymentId: string) => {
    navigate(`/admin/payments/${paymentId}/verify`);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="pending-payments-page">
      <div className="page-header">
        <h1>Pending Payment Verifications</h1>
        <Button onClick={() => navigate('/admin/dashboard')} variant="secondary">
          Back to Dashboard
        </Button>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      {payments.length === 0 ? (
        <div className="empty-state">
          <p>No pending payments to verify</p>
        </div>
      ) : (
        <>
          <div className="stats-row">
            <div className="stat-card">
              <span className="stat-label">Total Pending</span>
              <span className="stat-value">{payments.length}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Total Amount</span>
              <span className="stat-value">
                {formatCurrency(
                  payments.reduce((sum, p) => sum + parseFloat(p.amount), 0)
                )}
              </span>
            </div>
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Payment Ref</th>
                  <th>Loan Ref</th>
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>Bank</th>
                  <th>Transaction Ref</th>
                  <th>Payment Date</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.payment_id}>
                    <td>{payment.payment_reference}</td>
                    <td>{payment.loan_reference}</td>
                    <td>
                      <div className="customer-info">
                        <div className="customer-name">{payment.customer_name}</div>
                        <div className="customer-contact">
                          {payment.customer_email}
                          <br />
                          {payment.customer_phone}
                        </div>
                      </div>
                    </td>
                    <td>
                      {formatCurrency(payment.amount)}
                    </td>
                    <td>{payment.bank_name}</td>
                    <td>{payment.transaction_reference}</td>
                    <td>{new Date(payment.payment_date).toLocaleDateString()}</td>
                    <td>{new Date(payment.created_at).toLocaleDateString()}</td>
                    <td>
                      <Button
                        onClick={() => handleVerifyClick(payment.payment_id)}
                        variant="primary"
                        size="sm"
                      >
                        Verify
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <Button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                variant="secondary"
              >
                Previous
              </Button>
              <span className="page-info">
                Page {page} of {totalPages}
              </span>
              <Button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                variant="secondary"
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
